import { LocationObject } from '@pflegia/types/common/location/location-object';

/**
 * Transforms an array of LocationObject or a single LocationObject
 * into an object with arrays of values for each property.
 *
 * @param {LocationObject[] | unknown} answer - The array of LocationObjects or a single LocationObject.
 * @returns {Record<string, unknown[]>} An object with arrays of values for each property.
 * @example
 *  const answer = [
 *    {
 *      administrativeAreaLevel3: 'Berlin',
 *      city: 'Berlin',
 *      country: 'Germany',
 *      formattedAddress: 'Berlin, Germany',
 *      lat: 52.530180,
 *      lng: 13.383717,
 *      placeId: 'ChIJgQdUiuxRqEcRmdKseHzo6gI',
 *      postalCode: '11015',
 *      streetAddressAndNumber: 'Berlin, Germany',
 *    },
 *  ];
 *  locationPropertiesTransformer(answer);
 *  // {
 *  //   administrativeAreaLevel3: ['Berlin'],
 *  //   city: ['Berlin'],
 *  //   country: ['Germany'],
 *  //   formattedAddress: ['Berlin, Germany'],
 *  //   lat: [52.530180],
 *  //   lng: [13.383717],
 *  //   placeId: ['ChIJgQdUiuxRqEcRmdKseHzo6gI'],
 *  //   postalCode: ['11015'],
 *  //   streetAddressAndNumber: ['Berlin, Germany'],
 *  // }
 */
export const locationPropertiesTransformer = (answer: LocationObject[] | unknown): Record<string, unknown[]> => {
  // Cast the answer to an array of LocationObjects if it's not already an array.
  const locations = Array.isArray(answer) ? answer : [answer];

  // Initialize an empty accumulator object.
  const acc: Record<string, unknown[]> = {};

  // Iterate over each property.
  const keys = [
    'administrativeAreaLevel3',
    'city',
    'country',
    'formattedAddress',
    'lat',
    'lng',
    'placeId',
    'postalCode',
    'streetAddressAndNumber',
  ] as const;
  keys.forEach(key => {
    // If the property key doesn't exist in the accumulator object, initialize it as an empty array.
    if (!acc[key]) {
      acc[key] = [];
    }
    // Push the value of the property from the current location object to the accumulator array.
    locations.forEach(location => {
      acc[key].push(location[key]);
    });
  });

  // Return the accumulator object.
  return acc;
};
